import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge, ListGroup, Alert, Card } from 'react-bootstrap';
import api from '../../api/axios';
import { createJob, updateJob, deleteJob, fetchStaff } from '../../api/jobs';
import SettingsContext from '../../context/SettingsContext';
import { calculatePrices } from '../../utils/priceCalculator';
import JobServiceFields from '../job/JobServiceFields';
import { confirmUnpriced } from '../job/JobTiles';
import UnlinkButton from '../job/CustomerLink';
import AdjustmentInput from '../job/AdjustmentInput';
import useAdjustment from '../../hooks/useAdjustment';
import dayjs from 'dayjs';

const ReservationModal = ({ show, onHide, reservation, selectedDate, initialBay, onSaveSuccess }) => {
    const { settings } = useContext(SettingsContext);

    // 상태별 편집 권한 판단
    const resStatus = reservation?.status;
    // 입고 후(in_progress/completed): 메뉴/옵션/가격은 확정됨 → 읽기전용

    const [formData, setForm] = useState({
        customer_id: null, vehicle_id: null, customerName: '', phoneNumber: '', plateNumber: '',
        service_menu_id: '', staffId: '', bayNumber: 1, memo: '', status: 'confirmed',
        optionIds: [], depositAmount: 0,
        temp_manufacturer: '', temp_model: '', temp_size_id: '',
        date: '', time: '', duration: 60
    });

    // 지목한 고객에게 번호가 있었는지 — 없을 때만 연락처 칸을 열어 백필을 허용한다.
    const [linkedHasPhone, setLinkedHasPhone] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchHint, setSearchHint] = useState('');
    const identityRefs = useRef({}); // 검색 Enter 로 값을 넘긴 칸에 포커스를 주기 위한 참조

    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [prices, setPrices] = useState({ servicePrices: [], optionPrices: [] });
    const [staffList, setStaffList] = useState([]);
    const [carModels, setCarModels] = useState([]);
    const [vehicleSizes, setVehicleSizes] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [customerHistory, setCustomerHistory] = useState(null);
    const [missingFields, setMissingFields] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('카드');
    const adjustment = useAdjustment();
    // useCallback([]) 이라 참조가 안정적 — effect deps 에 그대로 넣는다
    const { reset: resetAdjustment } = adjustment;

    const initData = useCallback(async () => {
        try {
            const [mRes, oRes, pRes, sRes, cmRes, vsRes] = await Promise.all([
                api.get('/servicemenus'), api.get('/options'), api.get('/prices'),
                fetchStaff(), api.get('/carmodels'), api.get('/vehiclesizes')
            ]);
            setMenus(mRes.data.data.filter(m => m.isActive));
            setOptions(oRes.data.data.filter(o => o.isActive));
            setPrices(pRes.data.data);
            setStaffList(sRes);
            setCarModels(cmRes.data.data.filter(m => m.isActive));
            setVehicleSizes(vsRes.data.data.sizes || []);
        } catch (err) { console.error("Data load failed", err); }
    }, []);

    const fetchHistory = async (customerId, plateNumber) => {
        try {
            if (customerId) {
                const res = await api.get(`/customers/${customerId}`);
                setCustomerHistory({ type: 'customer', data: res.data.data });
            } else if (plateNumber) {
                const res = await api.get(`/vehicles/${plateNumber}`);
                setCustomerHistory({ type: 'vehicle', data: res.data.data });
            } else {
                setCustomerHistory(null);
            }
        } catch (err) {
            if (err.response?.status !== 404) console.error("History fetch failed", err);
            setCustomerHistory(null);
        }
    };

    useEffect(() => {
        if (show) {
            initData();
            if (reservation) {
                const start = dayjs(reservation.startTime);
                const end = dayjs(reservation.endTime);
                let opts = reservation.optionIds;
                if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch(e) { opts = []; } }
                
                setForm({
                    ...reservation,
                    vehicle_id: reservation.vehicleId || reservation.vehicle_id || null,
                    date: start.format('YYYY-MM-DD'),
                    time: start.format('HH:mm'),
                    duration: end.diff(start, 'minute'),
                    optionIds: Array.isArray(opts) ? opts : [],
                    staffId: reservation.staffId || '',
                    temp_manufacturer: reservation.temp_manufacturer || reservation.manufacturer || '',
                    temp_model: reservation.temp_model || reservation.modelName || '',
                    temp_size_id: reservation.temp_size_id || '',
                    service_menu_id: reservation.service_menu_id || reservation.courseId || '',
                    customerName: reservation.linkedCustomerName || reservation.customerName || '',
                    phoneNumber: reservation.phoneNumber || ''
                });
                fetchHistory(reservation.customer_id, reservation.plateNumber);
            } else {
                const start = dayjs(selectedDate || new Date());
                setForm({
                    customer_id: null, vehicle_id: null, customerName: '', phoneNumber: '', plateNumber: '',
                    service_menu_id: '', staffId: '', 
                    bayNumber: initialBay || 1,
                    memo: '', status: 'confirmed',
                    optionIds: [], depositAmount: 0,
                    temp_manufacturer: '', temp_model: '', temp_size_id: '',
                    date: start.format('YYYY-MM-DD'),
                    time: start.format('HH:mm'),
                    duration: 60
                });
                setCustomerHistory(null);
            }
            setError(''); setSuggestions([]);
            setLinkedHasPhone(!!reservation?.phoneNumber);
            setSearchTerm(''); setSearchHint('');
            setMissingFields([]);
            setPaymentMethod(reservation?.paymentMethod || '카드');
            resetAdjustment(reservation);
        }
    }, [show, reservation, selectedDate, initData, initialBay, resetAdjustment]);

    // 🚀 지능형 검색 및 디바운싱 적용
    const debouncedSearch = useMemo(() => {
        let timeoutId;
        return (val) => {
            if (timeoutId) clearTimeout(timeoutId);
            
            const cleanVal = val.trim().replace(/[^0-9a-zA-Z가-힣]/g, '');
            if (cleanVal.length < 2) {
                setSuggestions([]);
                return;
            }

            timeoutId = setTimeout(async () => {
                try {
                    const res = await api.get(`/vehicles/search?term=${encodeURIComponent(cleanVal)}`);
                    setSuggestions(res.data.data);
                } catch (err) {
                    console.error("Search failed", err);
                }
            }, 300);
        };
    }, []);

    const handleSearch = (val) => {
        setSearchTerm(val);
        setSearchHint('');
        debouncedSearch(val);
    };

    /**
     * 검색창에서 입력한 값이 차량번호/고객명/연락처 중 무엇인지 추측한다.
     *   숫자(+하이픈)만 & 10자 이상  → 연락처
     *   한글만 (숫자 없음)          → 고객명
     *   그 외 (12가3456, 4자리 숫자) → 차량번호
     */
    const guessIdentityField = (term) => {
        const t = term.trim();
        const digits = t.replace(/\D/g, '');
        if (/^[\d-]+$/.test(t) && digits.length >= 10) return 'phoneNumber';
        if (/[가-힣]/.test(t) && !/\d/.test(t)) return 'customerName';
        return 'plateNumber';
    };

    /**
     * 검색창 Enter.
     *   폼 submit 은 막는다 — 검색창은 기록의 일부가 아니다.
     *   제안이 있으면 첫 번째를 고른다 (목록에서 손으로 누르는 것과 동일).
     *   제안이 없으면 신규 고객이라는 뜻이므로, 입력값을 맞는 칸으로 옮기고 포커스를 준다.
     */
    const handleSearchKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const term = searchTerm.trim();
        if (!term) return;

        if (suggestions.length > 0) {
            handleSelectFound(suggestions[0]);
            setSearchTerm('');
            return;
        }

        const field = guessIdentityField(term);
        const label = { plateNumber: '차량번호', customerName: '고객명', phoneNumber: '연락처' }[field];
        handleFormChange(field, term);
        setSearchTerm('');
        setSearchHint(`등록된 기록이 없습니다 — ${label}에 넣었습니다. 나머지를 채워 새로 등록하세요.`);
        setTimeout(() => identityRefs.current[field]?.focus(), 0);
    };

    const handleSelectFound = (v) => {
        // 검색 결과 선택 시 기존 맵핑/폼을 전부 교체 (X 버튼 효과).
        // 이전 값을 유지(|| prev)하면 "김아무개 01022223333" 같은 혼종 상태가 생김.
        setForm(prev => ({
            ...prev,
            vehicle_id: v.id ?? null,
            customer_id: v.customer_id ?? null,
            plateNumber: v.plateNumber || '',
            customerName: v.customerName || '',
            phoneNumber: v.phoneNumber || '',
            temp_manufacturer: v.manufacturer || '',
            temp_model: v.modelName || '',
            temp_size_id: v.vehicle_size_id || ''
        }));
        setLinkedHasPhone(!!v.phoneNumber);
        setSuggestions([]);

        // 🚀 히스토리 패널 업데이트 (고객 ID가 없어도 차량 번호로 조회)
        fetchHistory(v.customer_id, v.plateNumber);
    };

    // 고객 연결 해제 — 입력값은 남기고 연결만 끊는다. 이후 저장 시 이름·번호로 다시 찾는다.
    const handleUnlinkCustomer = () => {
        setForm(prev => ({ ...prev, customer_id: null }));
        setLinkedHasPhone(false);
        if (formData.plateNumber) fetchHistory(null, formData.plateNumber);
        else setCustomerHistory(null);
    };

    // 🚀 입력값 수동 변경 시 처리 로직 강화
    const handleFormChange = (field, value) => {
        setForm(prev => {
            const newState = { ...prev, [field]: value };

            // 연결은 손으로 고쳐서 풀리지 않는다 — ✕(handleUnlinkCustomer)로만 해제한다.
            //   자동 해제를 두면 '이름만 있는 고객에게 번호 받아 적기'에서 연결이 조용히 풀려
            //   동명이인이 또 생긴다. 연결 중에는 이름 칸이 잠겨 있고, 연락처는 비어 있을 때만 열린다.
            if ((field === 'customerName' || field === 'phoneNumber') && value === '' && !newState.customer_id) {
                if (!newState.customerName && !newState.phoneNumber && newState.plateNumber) {
                    fetchHistory(null, newState.plateNumber);
                } else if (!newState.plateNumber) {
                    setCustomerHistory(null);
                }
            }

            // 전화번호 직접 입력 시 기존 고객 검색
            if (field === 'phoneNumber' && !newState.customer_id) {
                const digits = value.replace(/\D/g, '');
                if (digits.length >= 10) {
                    api.get(`/vehicles/search?term=${encodeURIComponent(digits)}`)
                        .then(res => {
                            const match = res.data.data?.[0];
                            if (match?.customer_id) fetchHistory(match.customer_id, null);
                            else setCustomerHistory(null);
                        })
                        .catch(() => {});
                } else if (digits.length === 0) {
                    setCustomerHistory(null);
                }
            }

            // 차량번호가 수동 변경되면 기존 차량 매핑 해제
            if (field === 'plateNumber') {
                if (prev.vehicle_id) {
                    newState.vehicle_id = null;
                    newState.temp_manufacturer = '';
                    newState.temp_model = '';
                    newState.temp_size_id = '';
                }
                if (value.length >= 4) {
                    if (!newState.customer_id) fetchHistory(null, value);
                } else {
                    if (!newState.customer_id) setCustomerHistory(null);
                }
            }

            return newState;
        });
    };

    const updateVehicleDB = (vehicleId, fields) => {
        if (!vehicleId) return;
        api.put(`/vehicles/${vehicleId}`, fields).catch(err => console.error('차량 정보 업데이트 실패', err));
    };

    const handleManufacturerChange = (value) => {
        setForm(prev => ({ ...prev, temp_manufacturer: value, temp_model: '' }));
        updateVehicleDB(formData.vehicle_id, { carModelId: null });
    };

    const handleModelChange = (value) => {
        setForm(prev => ({ ...prev, temp_model: value }));
        const matched = carModels.find(m => m.manufacturer === formData.temp_manufacturer && m.modelName === value);
        if (matched) updateVehicleDB(formData.vehicle_id, { carModelId: matched.id });
    };

    const handleSizeChange = (value) => {
        setForm(prev => ({ ...prev, temp_size_id: value }));
        updateVehicleDB(formData.vehicle_id, { vehicle_size_id: value || null });
    };

    const applyVehicleFromHistory = (v) => {
        setForm(prev => ({
            ...prev,
            vehicle_id: v.id,
            plateNumber: v.plateNumber,
            temp_manufacturer: v.manufacturer || '',
            temp_model: v.modelName || '',
            temp_size_id: v.vehicle_size_id || prev.temp_size_id
        }));
    };

    const { totalPrice } = useMemo(() => {
        const sizeId = formData.temp_size_id;
        if (!sizeId || !formData.service_menu_id) return { totalPrice: 0 };
        const result = calculatePrices({
            selectedMenu: menus.find(m => m.id === parseInt(formData.service_menu_id)),
            selectedOptions: options.filter(o => formData.optionIds.includes(o.id)),
            prices, sizeId, adjustmentAmount: adjustment.signed
        });
        return { totalPrice: result.totalPrice };
    }, [formData, menus, options, prices, adjustment.signed]);

    const handleSubmit = async (e) => {
        if(e) e.preventDefault();

        // 입고/완료 상태에서 서비스 내용 변경 시 확인
        if (reservation && (resStatus === 'in_progress' || resStatus === 'completed')) {
            const menuChanged = String(formData.service_menu_id) !== String(reservation.service_menu_id || reservation.courseId || '');
            const optsOld = JSON.stringify((reservation.optionIds || []).sort());
            const optsNew = JSON.stringify((formData.optionIds || []).sort());
            if (menuChanged || optsOld !== optsNew) {
                if (!window.confirm('서비스 내용 및 금액이 변경됩니다. 계속하시겠습니까?')) return;
            }
        }

        if (!formData.plateNumber && !formData.customerName && !formData.phoneNumber) {
            setMissingFields(['plateNumber']);
            setError('차량번호, 고객명, 연락처 중 하나는 입력해주세요.');
            return;
        }

        setLoading(true);
        setError('');

        let submissionData = {
            ...formData,
            // undefined 로 둬야 express-validator 의 .optional() 이 건너뛴다.
            // null 을 보내면 courseId.isInt() 가 걸려 영어 'Invalid value' 가 그대로 노출된다.
            courseId: formData.service_menu_id ? parseInt(formData.service_menu_id) : undefined,
            staffId: formData.staffId === '' ? null : formData.staffId,
            finalPrice: totalPrice,
            paymentMethod,
            adjustmentAmount: adjustment.signed,
        };

        // date + time 조합으로 startTime/endTime 계산 (신규/수정 공통)
        const startStr = `${formData.date} ${formData.time}`;
        submissionData.startTime = dayjs(startStr).format('YYYY-MM-DD HH:mm:ss');
        submissionData.endTime = dayjs(startStr).add(formData.duration, 'minute').format('YYYY-MM-DD HH:mm:ss');

        // 저장 완료는 데이터 수정만 담당 — 상태 전환은 전용 액션 버튼에서 처리
        if (reservation) delete submissionData.status;

        try {
            if (reservation) await updateJob(reservation.id, submissionData);
            else await createJob(submissionData);
            onSaveSuccess(); onHide();
        } catch (err) {
            const errorMsg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || '저장 실패';
            setError(errorMsg);
        } finally { setLoading(false); }
    };

    const handleStartWork = async () => {
        const missing = [];
        if (!formData.plateNumber) missing.push('plateNumber');
        if (settings?.is_car_model_required && !formData.temp_manufacturer) missing.push('temp_manufacturer');
        if (settings?.is_car_model_required && !formData.temp_model) missing.push('temp_model');
        if (!formData.temp_size_id) missing.push('temp_size_id');
        if (!formData.service_menu_id) missing.push('service_menu_id');

        if (missing.length > 0) {
            setMissingFields(missing);
            setError(settings?.is_car_model_required
                ? '차량번호, 차종(제조사/모델), 사이즈, 메뉴를 모두 입력해주세요.'
                : '차량번호, 사이즈, 메뉴를 모두 입력해주세요.');
            return;
        }

        // 가격표에 없는 조합이면 0원으로 계산된다는 걸 알린다 (차단 아님)
        if (!confirmUnpriced({ menus, options, prices, sizeId: formData.temp_size_id,
            menuId: formData.service_menu_id, optionIds: formData.optionIds || [] })) return;

        setLoading(true);
        setError('');

        try {
            let currentVehicleId = formData.vehicle_id;
            let currentReservationId = reservation?.id;

            // 🚀 만약 vehicle_id가 없다면(수동 입력 중인 경우), 먼저 예약을 저장하여 차량 및 고객을 생성/연결함
            if (!currentVehicleId || !currentReservationId) {
                console.log("Vehicle or Reservation ID missing. Saving first...");
                const startStr = `${formData.date} ${formData.time}`;
                const submissionData = {
                    ...formData,
                    courseId: formData.service_menu_id || null,
                    staffId: formData.staffId === '' ? null : formData.staffId,
                    startTime: dayjs(startStr).format('YYYY-MM-DD HH:mm:ss'),
                    endTime: dayjs(startStr).add(formData.duration, 'minute').format('YYYY-MM-DD HH:mm:ss')
                };

                let savedRes;
                if (reservation) {
                    savedRes = await updateJob(reservation.id, submissionData);
                } else {
                    savedRes = await createJob(submissionData);
                }
                
                // 저장 후 반환된 최신 정보를 반영
                currentVehicleId = savedRes?.vehicle_id || currentVehicleId;
                currentReservationId = savedRes?.id || currentReservationId;
                
                if (!currentVehicleId) {
                    throw new Error("차량 정보를 생성할 수 없습니다. 제조사/모델/사이즈를 확인해주세요.");
                }
            }

            // 🚀 기존 Job을 입고 상태로 전이 + 정산 데이터 추가
            await updateJob(currentReservationId, {
                status: 'in_progress',
                vehicleId: currentVehicleId,
                courseId: parseInt(formData.service_menu_id),
                optionIds: formData.optionIds || [],
                finalPrice: totalPrice,
                comment: formData.memo || '',
                paymentMethod,
                adjustmentAmount: adjustment.signed,
                adjustmentReason: adjustment.reason,
                customerName: formData.customerName || '',
                phoneNumber: formData.phoneNumber || '',
                // 입고 시 폼에 입력한 차종/사이즈를 차량에 반영 (예약 시 미정이었던 경우 여기서 채움)
                plateNumber: formData.plateNumber,
                temp_manufacturer: formData.temp_manufacturer,
                temp_model: formData.temp_model,
                temp_size_id: formData.temp_size_id,
            });
            onSaveSuccess();
            onHide();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || '입고 처리 실패';
            setError(msg);
        } finally { setLoading(false); }
    };

    const manufacturers = useMemo(() => {
        // API가 displayOrder 순으로 반환 → 등장 순서 유지
        const seen = new Set();
        const list = [];
        carModels.forEach(m => {
            if (m.manufacturer && !seen.has(m.manufacturer)) {
                seen.add(m.manufacturer);
                list.push(m.manufacturer);
            }
        });
        return list;
    }, [carModels]);
    const filteredModels = carModels.filter(m => m.manufacturer === formData.temp_manufacturer);

    // --- 상태 전환 즉시 액션 핸들러들 ---

    const handleMarkNoshow = async () => {
        if (!window.confirm('노쇼 처리하시겠습니까?')) return;
        setLoading(true);
        try {
            await updateJob(reservation.id, { status: 'noshow' });
            onSaveSuccess(); onHide();
        } catch (err) {
            setError(err.response?.data?.error || '노쇼 처리 실패');
        } finally { setLoading(false); }
    };

    const handleComplete = async () => {
        const missing = [];
        if (!formData.plateNumber) missing.push('차량번호');
        if (settings?.is_car_model_required && !formData.temp_manufacturer) missing.push('제조사');
        if (settings?.is_car_model_required && !formData.temp_model) missing.push('모델');
        if (!formData.temp_size_id) missing.push('사이즈');
        if (!formData.service_menu_id) missing.push('메뉴');
        if (missing.length > 0) {
            setMissingFields(missing.map(m => {
                const map = { '차량번호': 'plateNumber', '제조사': 'temp_manufacturer', '모델': 'temp_model', '사이즈': 'temp_size_id', '메뉴': 'service_menu_id' };
                return map[m] || m;
            }));
            setError(`[${missing.join(', ')}] 정보가 누락되었습니다.`);
            return;
        }

        // 가격표에 없는 조합이면 0원으로 계산된다는 걸 알린다 (차단 아님)
        if (!confirmUnpriced({ menus, options, prices, sizeId: formData.temp_size_id,
            menuId: formData.service_menu_id, optionIds: formData.optionIds || [] })) return;

        if (!window.confirm('작업을 완료 처리하시겠습니까?')) return;
        setLoading(true);
        try {
            // 지능형 종료 시간 조정
            const now = dayjs();
            const plannedEnd = dayjs(formData.date + ' ' + formData.time).add(formData.duration, 'minute');
            const updates = {
                status: 'completed',
                // 완료 시 폼에 입력한 차종/사이즈를 차량에 반영 (입고 생략 후 바로 완료한 경우 대비)
                plateNumber: formData.plateNumber,
                temp_manufacturer: formData.temp_manufacturer,
                temp_model: formData.temp_model,
                temp_size_id: formData.temp_size_id,
            };
            if (now.isBefore(plannedEnd)) {
                const newDuration = Math.max(10, now.diff(dayjs(formData.date + ' ' + formData.time), 'minute'));
                updates.endTime = dayjs(formData.date + ' ' + formData.time).add(newDuration, 'minute').format('YYYY-MM-DD HH:mm:ss');
            }
            await updateJob(reservation.id, updates);
            onSaveSuccess(); onHide();
        } catch (err) {
            setError(err.response?.data?.error || '완료 처리 실패');
        } finally { setLoading(false); }
    };

    const handleCancelWork = async () => {
        if (!window.confirm('작업을 취소하시겠습니까?')) return;
        setLoading(true);
        try {
            await updateJob(reservation.id, { status: 'confirmed' });
            onSaveSuccess(); onHide();
        } catch (err) {
            setError(err.response?.data?.error || '작업 취소 실패');
        } finally { setLoading(false); }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="washnote-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold text-primary"><i className="bi bi-headset me-2"></i>예약 상담 및 등록</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="pt-3">
                    {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                    
                    <Row className="g-4">
                        {/* 📝 좌측: 예약 폼 영역 (7/12) */}
                        <Col lg={7} className="border-end pr-lg-4">
                            {reservation && (
                                <div className="mb-4">
                                    <Badge bg={
                                        formData.status === 'completed' ? 'success' :
                                        formData.status === 'in_progress' ? 'primary' :
                                        formData.status === 'noshow' ? 'danger' :
                                        formData.status === 'canceled' ? 'secondary' : 'warning'
                                    } className="px-3 py-2 fs-6">
                                        {formData.status === 'pending' ? '대기' : formData.status === 'confirmed' ? '확정' : formData.status === 'in_progress' ? '작업중' : formData.status === 'completed' ? '완료' : formData.status === 'noshow' ? '노쇼' : '취소'}
                                    </Badge>
                                </div>
                            )}

                            <h6 className="fw-bold mb-3 text-muted small uppercase"><i className="bi bi-search me-2"></i>빠른 고객/차량 검색</h6>
                            <div className="position-relative mb-4">
                                <Form.Control 
                                    placeholder="차량번호, 고객명, 또는 전화번호 입력..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    className="bg-light border-0 py-2 shadow-sm"
                                />
                                {searchHint && (
                                    <div className="small text-muted mt-1">
                                        <i className="bi bi-info-circle me-1"></i>{searchHint}
                                    </div>
                                )}
                                {suggestions.length > 0 && (
                                    <ListGroup className="position-absolute w-100 shadow-lg z-3 mt-1">
                                        {suggestions.map(v => (
                                            <ListGroup.Item key={v.id} action onClick={() => handleSelectFound(v)} className="small d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong className="text-primary">{v.plateNumber}</strong>
                                                    <span className="ms-2 text-muted">{v.customerName || '이름없음'}</span>
                                                    <small className="ms-2 text-muted opacity-75">{v.phoneNumber}</small>
                                                </div>
                                                <Badge bg="secondary" pill className="fw-normal">선택</Badge>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>

                            <Row className="g-3 mb-3">
                                <Col md={4}><Form.Group><Form.Label className="small fw-bold">차량번호</Form.Label><Form.Control type="text" ref={el => { identityRefs.current.plateNumber = el; }} value={formData.plateNumber} onChange={e => { handleFormChange('plateNumber', e.target.value); setMissingFields(prev => prev.filter(f => f !== 'plateNumber')); }} className="bg-light border-0" isInvalid={missingFields.includes('plateNumber')} /></Form.Group></Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">고객명</Form.Label>
                                        <div className="cl-wrap">
                                            <Form.Control type="text" ref={el => { identityRefs.current.customerName = el; }}
                                                value={formData.customerName}
                                                onChange={e => handleFormChange('customerName', e.target.value)}
                                                readOnly={!!formData.customer_id}
                                                className={formData.customer_id ? 'cl-locked' : 'bg-light border-0'} />
                                            {formData.customer_id && <UnlinkButton onUnlink={handleUnlinkCustomer} />}
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">연락처</Form.Label>
                                        <Form.Control type="text" ref={el => { identityRefs.current.phoneNumber = el; }}
                                            value={formData.phoneNumber}
                                            onChange={e => handleFormChange('phoneNumber', e.target.value)}
                                            readOnly={!!formData.customer_id && linkedHasPhone}
                                            className={(formData.customer_id && linkedHasPhone) ? 'cl-locked' : 'bg-light border-0'} />
                                        {formData.customer_id && !linkedHasPhone && (
                                            <div className="cl-hint mt-1">
                                                <i className="bi bi-info-circle me-1"></i>입력하면 이 고객에게 저장됩니다
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">제조사{!settings?.is_car_model_required && <span className="text-muted small ms-1">(선택사항)</span>}</Form.Label>
                                        <Form.Select
                                            value={formData.temp_manufacturer}
                                            onChange={e => { handleManufacturerChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_manufacturer')); }}
                                            className="bg-light border-0"
                                            isInvalid={missingFields.includes('temp_manufacturer')}
                                        >
                                            <option value="">선택</option>
                                            {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">모델{!settings?.is_car_model_required && <span className="text-muted small ms-1">(선택사항)</span>}</Form.Label>
                                        <Form.Select
                                            value={formData.temp_model}
                                            onChange={e => { handleModelChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_model')); }}
                                            className="bg-light border-0"
                                            disabled={!formData.temp_manufacturer}
                                            isInvalid={missingFields.includes('temp_model')}
                                        >
                                            <option value="">선택</option>
                                            {filteredModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">사이즈</Form.Label>
                                        <Form.Select
                                            value={formData.temp_size_id}
                                            onChange={e => { handleSizeChange(e.target.value); setMissingFields(prev => prev.filter(f => f !== 'temp_size_id')); }}
                                            className="bg-light border-0"
                                            isInvalid={missingFields.includes('temp_size_id')}
                                        >
                                            <option value="">선택</option>
                                            {vehicleSizes.map(s => <option key={s.id} value={s.id}>{s.sizeName}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <h6 className="fw-bold mb-3 text-muted small uppercase"><i className="bi bi-clock me-2"></i>작업 일정 설정</h6>
                            <Row className="g-3 mb-4">
                                <Col md={4}><Form.Group><Form.Label className="small fw-bold">날짜</Form.Label><Form.Control type="date" value={formData.date} onChange={e => setForm({...formData, date: e.target.value})} className="bg-light border-0 shadow-sm" /></Form.Group></Col>
                                <Col md={3}><Form.Group><Form.Label className="small fw-bold">시간</Form.Label><Form.Control type="time" value={formData.time} onChange={e => setForm({...formData, time: e.target.value})} className="bg-light border-0 shadow-sm" /></Form.Group></Col>
                                <Col md={3}><Form.Group><Form.Label className="small fw-bold">소요시간</Form.Label><div className="d-flex align-items-center gap-1"><Form.Control type="number" min={30} step={30} value={formData.duration} onChange={e => setForm({...formData, duration: parseInt(e.target.value) || ''})} onBlur={e => { const v = parseInt(e.target.value); setForm(prev => ({...prev, duration: Math.max(10, v || 10)})); }} className="bg-light border-0 shadow-sm" style={{ width: '70px' }} /><span className="text-muted small">분</span></div></Form.Group></Col>
                                <Col md={2}><Form.Group><Form.Label className="small fw-bold">베이</Form.Label><Form.Select value={formData.bayNumber} onChange={e => setForm({...formData, bayNumber: parseInt(e.target.value)})} className="bg-light border-0 shadow-sm">{[...Array(settings?.bay_count || 1)].map((_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}</Form.Select></Form.Group></Col>
                            </Row>

                            <h6 className="fw-bold mb-3 text-muted small uppercase"><i className="bi bi-gear me-2"></i>서비스 및 작업자</h6>
                            <JobServiceFields
                                menus={menus}
                                options={options}
                                prices={prices}
                                sizeId={formData.temp_size_id}
                                value={{ service_menu_id: formData.service_menu_id, optionIds: formData.optionIds }}
                                onChange={(patch) => {
                                    setForm(prev => ({ ...prev, ...patch }));
                                    if ('service_menu_id' in patch) setMissingFields(prev => prev.filter(f => f !== 'service_menu_id'));
                                }}
                                menuInvalid={missingFields.includes('service_menu_id')}
                                compact
                            />

                            <Row className="g-3 mb-4">
                                <Col md={5}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">작업자</Form.Label>
                                        <Form.Select value={formData.staffId} onChange={e => setForm({...formData, staffId: e.target.value})} className="bg-light border-0 shadow-sm small">
                                            <option value="">미지정</option>
                                            {staffList.map(s => <option key={s.id} value={s.id}>{s.owner_name || s.username}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Control as="textarea" rows={2} placeholder="기타 상담 메모..." value={formData.memo} onChange={e => setForm({...formData, memo: e.target.value})} className="bg-light border-0 py-2 small" />
                        </Col>

                        {/* 📊 우측: CRM 패널 (5/12) */}
                        <Col lg={5} className="bg-light rounded-3 p-3 d-flex flex-column" style={{ height: 'auto', maxHeight: '700px' }}>
                            <h6 className="fw-bold mb-3 text-muted small uppercase flex-shrink-0"><i className="bi bi-person-lines-fill me-2"></i>고객 히스토리 (CRM)</h6>
                            
                            {!customerHistory ? (
                                <div className="flex-grow-1 d-flex align-items-center justify-content-center text-center p-4 text-muted border border-dashed rounded bg-white opacity-50">
                                    <div>
                                        <i className="bi bi-person-search fs-1"></i>
                                        <p className="small mt-2">상단에서 고객을 검색하거나 선택하면<br/>보유 차량과 방문 기록이 노출됩니다.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                                    {customerHistory.type === 'customer' && (
                                        <>
                                            {/* 고객 기본 요약 */}
                                            <Card className="border-0 shadow-sm bg-primary text-white mb-3 flex-shrink-0">
                                                <Card.Body className="p-3">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <h5 className="mb-0 fw-bold">{customerHistory.data.customer.customerName}</h5>
                                                            <small className="opacity-75">{customerHistory.data.customer.phoneNumber}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <Badge bg="light" text="primary" className="p-2">{customerHistory.data.customer.points.toLocaleString()} P</Badge>
                                                            {customerHistory.data.customer.noshowCount > 0 && (
                                                                <div className="mt-1"><Badge bg="danger" className="shadow-sm">노쇼 {customerHistory.data.customer.noshowCount}회</Badge></div>
                                                            )}
                                                            {customerHistory.data.customer.canceledCount > 0 && (
                                                                <div className="mt-1"><Badge bg="secondary" className="shadow-sm">취소 {customerHistory.data.customer.canceledCount}회</Badge></div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>

                                            {/* 보유 차량 목록 */}
                                            <div className="mb-3 flex-shrink-0">
                                                <div className="small fw-bold text-primary mb-2">보유 차량 ({customerHistory.data.vehicles.length})</div>
                                                <ListGroup variant="flush" className="border rounded bg-white shadow-sm overflow-hidden">
                                                    {customerHistory.data.vehicles.map(v => (
                                                        <ListGroup.Item key={v.id} className="d-flex justify-content-between align-items-center p-2 small">
                                                            <div>
                                                                <span className="fw-bold">{v.plateNumber}</span>
                                                                <span className="ms-2 text-muted">{v.manufacturer} {v.modelName}</span>
                                                            </div>
                                                            <Button size="sm" variant="outline-primary" className="py-0 px-2 border-0" onClick={() => applyVehicleFromHistory(v)}>
                                                                <i className="bi bi-plus-circle-fill fs-5"></i>
                                                            </Button>
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            </div>
                                        </>
                                    )}

                                    {customerHistory.type === 'vehicle' && (
                                        <Card className="border-0 shadow-sm bg-secondary bg-opacity-10 mb-3 flex-shrink-0 border-start border-secondary border-4">
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <Badge bg="secondary" className="mb-1">미등록 방문 차량</Badge>
                                                        <h5 className="mb-0 fw-bold text-dark">{customerHistory.data.plateNumber}</h5>
                                                    </div>
                                                    <div className="text-end">
                                                        {customerHistory.data.noshowCount > 0 && (
                                                            <div className="mt-1"><Badge bg="danger" className="shadow-sm">노쇼 {customerHistory.data.noshowCount}회</Badge></div>
                                                        )}
                                                        {customerHistory.data.canceledCount > 0 && (
                                                            <div className="mt-1"><Badge bg="secondary" className="shadow-sm">취소 {customerHistory.data.canceledCount}회</Badge></div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    {/* 서비스 기록 */}
                                    <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
                                        <div className="small fw-bold text-danger mb-2 flex-shrink-0">
                                            과거 방문 기록 ({customerHistory.data.serviceHistory.length}건)
                                        </div>
                                        {customerHistory.data.serviceHistory.length === 0 ? (
                                            <div className="text-center p-3 small text-muted bg-white border rounded flex-shrink-0">기록이 없습니다.</div>
                                        ) : (
                                            <div className="overflow-auto pe-2" style={{ flex: 1 }}>
                                                {customerHistory.data.serviceHistory.map(h => {
                                                    const isWalkIn = h.source === 'walk-in';
                                                    return (
                                                    <div key={h.id} className="bg-white p-2 rounded shadow-sm mb-2 border-start border-primary border-4 small">
                                                        <div className="d-flex justify-content-between align-items-center mb-1 fw-bold">
                                                            <div>
                                                                <Badge bg={isWalkIn ? "secondary" : "info"} className="me-2 fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                    {isWalkIn ? '현장' : '예약'}
                                                                </Badge>
                                                                <span>{h.menuName}</span>
                                                            </div>
                                                            <span className="text-primary">{parseFloat(h.finalPrice).toLocaleString()}원</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.7rem' }}>
                                                            <span>{dayjs(h.startTime || h.created_at).format('YYYY-MM-DD')}</span>
                                                            <span>{h.paymentMethod}</span>
                                                        </div>
                                                        {(h.optionsText || h.selectedOptions) && <div className="text-muted" style={{ fontSize: '0.7rem' }}>옵션: {h.optionsText || h.selectedOptions}</div>}
                                                        {h.comment && <div className="mt-1 p-1 bg-light rounded text-muted" style={{ fontSize: '0.65rem', borderLeft: '2px solid #ddd' }}>{h.comment}</div>}
                                                    </div>
                                                )})}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 px-4 pb-4">
                    <div className="w-100">
                        {(!reservation || ['pending', 'confirmed', 'in_progress', 'completed'].includes(reservation.status)) && (
                            <div className="d-flex gap-3 align-items-center p-3 mb-3 bg-light rounded-3 flex-wrap">
                                <small className="fw-bold text-muted text-nowrap">
                                    {resStatus === 'in_progress' || resStatus === 'completed' ? '입고 정산' : '예상 금액'}
                                </small>
                                <Form.Select size="sm" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={{ width: 'auto' }}>
                                    <option value="카드">카드</option>
                                    <option value="현금">현금</option>
                                    <option value="이체">이체</option>
                                    <option value="포인트">포인트</option>
                                </Form.Select>
                                <AdjustmentInput
                                    adjustment={adjustment}
                                    inputStyle={{ width: '110px' }}
                                />
                                <span className="ms-auto fw-bold text-primary fs-5">
                                    {totalPrice > 0 ? `${totalPrice.toLocaleString()}원` : '메뉴/사이즈 선택 필요'}
                                </span>
                            </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-2">
                                {/* 대기/확정: 예약 취소, 노쇼 처리 */}
                                {reservation && (resStatus === 'pending' || resStatus === 'confirmed') && (
                                    <>
                                        <Button variant="outline-danger" size="sm" onClick={async () => { if(window.confirm('예약을 취소하시겠습니까?')) { await deleteJob(reservation.id); onSaveSuccess(); onHide(); } }} disabled={loading}>예약 취소</Button>
                                        <Button variant="outline-dark" size="sm" onClick={handleMarkNoshow} disabled={loading}>노쇼 처리</Button>
                                    </>
                                )}
                                {/* 작업중/완료: 작업 취소 */}
                                {reservation && (resStatus === 'in_progress' || resStatus === 'completed') && (
                                    <Button variant="outline-danger" size="sm" onClick={handleCancelWork} disabled={loading}>작업 취소</Button>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                {/* 대기/확정: 작업 시작 */}
                                {reservation && (resStatus === 'pending' || resStatus === 'confirmed') && (
                                    <Button variant="success" onClick={handleStartWork} disabled={loading} className="px-4 fw-bold shadow-sm">
                                        <i className="bi bi-play-fill me-1"></i>작업 시작 (입고)
                                    </Button>
                                )}
                                {/* 작업중: 작업 완료 */}
                                {reservation && resStatus === 'in_progress' && (
                                    <Button variant="success" onClick={handleComplete} disabled={loading} className="px-4 fw-bold shadow-sm">
                                        <i className="bi bi-check-lg me-1"></i>작업 완료
                                    </Button>
                                )}
                                <Button variant="light" onClick={onHide} className="px-4 border">닫기</Button>
                                <Button variant="primary" type="submit" disabled={loading} className="px-5 fw-bold shadow-sm">{loading ? <Spinner animation="border" size="sm" /> : '저장'}</Button>
                            </div>
                        </div>
                    </div>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ReservationModal;
